/** @id on the first occurrence, @ref on every one after it: Jackson's object identity. */
const ID = '@id';
const REF = '@ref';

/**
 * Replaces every {@code {"@ref": "2"}} with the object that carried that {@code @id}.
 *
 * <p>unimarket-api serialises a shared object once and refers back to it afterwards, and a page
 * routinely carries the same one twice -- a website page is inside `website.websitePages` and
 * again as the page being shown. The second occurrence arrives as a stub with no fields, so the
 * page renders empty rather than failing, which is why this is worth doing for every response
 * rather than at the one call site that noticed.
 *
 * <p>Jackson writes the full object before any reference to it, so one pass in document order is
 * enough. Objects are patched in place: a reference resolved before the object is fully walked
 * still ends up pointing at the finished one.
 */
export function resolveReferences<T>(body: T): T {
  return walk(body, new Map<string, unknown>()) as T;
}

function walk(value: unknown, byId: Map<string, unknown>): unknown {
  if (Array.isArray(value)) {
    return value.map(item => walk(item, byId));
  }
  if (!value || typeof value !== 'object') {
    return value;
  }
  const object = value as Record<string, unknown>;
  const reference = object[REF];
  if (typeof reference === 'string' && byId.has(reference)) {
    return byId.get(reference);
  }
  const id = object[ID];
  if (typeof id === 'string') {
    byId.set(id, object);
  }
  for (const key of Object.keys(object)) {
    object[key] = walk(object[key], byId);
  }
  return object;
}
